import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  ArrowLeftRight,
  Eye,
  Users,
  Gamepad2
} from 'lucide-react';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Why We Built Compare the Pair: A Daily Stock Comparison Game",
  "description": "Learn why we created Compare the Pair, a daily head-to-head stock comparison game that teaches you to evaluate companies using real financial metrics.",
  "url": "https://wallstreetle.com/blog/compare-the-pair",
  "datePublished": "2026-04-17",
  "dateModified": "2026-04-17",
  "author": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "publisher": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "image": "https://wallstreetle.com/assets/share-link-image.png"
};

export default function CompareThePairBlogPage() {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Why We Built Compare the Pair</title>
        <meta
          name="description"
          content="Discover why we built Compare the Pair, a daily game where you compare two stocks head-to-head on real financial metrics like revenue, market cap, and P/E ratio."
        />
        <meta
          name="keywords"
          content="compare stocks, stock comparison game, head to head stocks, which stock is better, stock metrics comparison, compare companies, stock market game, financial metrics quiz"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog/compare-the-pair" />

        <meta property="og:title" content="Wallstreetle | Why We Built Compare the Pair" />
        <meta property="og:description" content="A daily game where you compare two stocks head-to-head on real financial metrics. Learn to evaluate companies like an investor." />
        <meta property="og:url" content="https://wallstreetle.com/blog/compare-the-pair" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Why We Built Compare the Pair" />
        <meta property="twitter:description" content="A daily head-to-head stock comparison game using real financial data." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <ArrowLeftRight className="w-4 h-4" /> Behind the Game
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            Why We Built Compare the Pair
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Most people study stocks in isolation. Real investing is about relative comparison, and that's the skill we wanted to teach.
          </p>
        </div>

        {/* Section: The Problem */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">The problem I kept seeing</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              When most people research a stock, they look at it in a vacuum. They'll check a company's revenue, read its P/E ratio, maybe glance at the market cap. But those numbers mean very little on their own.
            </p>
            <p>
              Is a $50 billion market cap "big"? Is a P/E of 25 "expensive"? The answer always depends on what you're comparing it to. A P/E of 25 looks reasonable next to a high-growth tech company at 80x earnings, but expensive compared to a mature bank at 10x.
            </p>
            <p>
              Professional investors think in relatives. They compare companies within the same industry, stacking metrics side by side to find the better opportunity. I wanted to build a game that trains that exact instinct.
            </p>
          </div>
        </section>

        {/* Section: How It Works */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">How Compare the Pair works</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { step: '1', title: 'Two companies, same industry', desc: 'Each day you get a fresh pair of companies from the same sector, so comparisons are meaningful and fair.' },
              { step: '2', title: '10 metric questions', desc: 'You\'re asked "Which company has higher revenue?", "Which has a lower P/E ratio?", and more. Guess correctly to build your score.' },
              { step: '3', title: 'Community vote', desc: 'After the metrics, there\'s a final opinion question where the community votes. See how your view stacks up against everyone else.' },
              { step: '4', title: 'Learn from results', desc: 'Review a summary of all 10 metrics with AI-generated insights explaining why each comparison matters.' },
            ].map((item) => (
              <Card key={item.step} className="p-5">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 font-bold text-sm shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Section: What Players Learn */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">What players actually learn</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {[
              { title: 'Reading financials in context', desc: 'Revenue, earnings, and margins only make sense when compared to a peer. You\'ll build intuition for what "good" looks like in each industry.', icon: Eye, color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' },
              { title: 'Relative valuation', desc: 'Learn to spot when a stock is cheap or expensive relative to its competitor, using metrics like P/E, P/S, and EV/EBITDA.', icon: ArrowLeftRight, color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' },
              { title: 'Market awareness', desc: 'Over weeks of playing, you\'ll absorb the rough size, profitability, and valuation of hundreds of real public companies.', icon: Users, color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="p-6 hover:shadow-lg transition-shadow">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${item.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: Why It Matters */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Why comparison is the best teacher</h2>
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/50 rounded-lg shrink-0">
                <Gamepad2 className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  When you see that Company A's revenue is $30 billion and Company B's is $8 billion, your brain doesn't just memorise the numbers. It starts building a mental model of the industry: who's dominant, who's the challenger, and what the gap means for valuation.
                </p>
                <p>
                  That's the kind of <strong className="text-foreground">contextual financial literacy</strong> that textbooks struggle to teach. Compare the Pair delivers it in a two-minute daily game.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
            Ready to compare your first pair?
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Today's matchup is live. See if you can guess which company comes out on top across 10 real financial metrics.
          </p>
          <Link
            to="/compare-the-pair"
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Play Compare the Pair
          </Link>
        </Card>
      </div>
    </>
  );
}
